import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from './../assets/Logo.png';
import axios from 'axios';
import { API_BASE_URL } from './../config';
import './../Styles/Navbar.css';

import { useUser } from './../Context/UserContext';
import { useCart } from './../Context/CartContext';

const Navbar = () => {
  const location = useLocation();
  const { user, logout } = useUser();
  const { cartCount } = useCart();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [hoveredSubCategory, setHoveredSubCategory] = useState(null);
  const [hoveredUser, setHoveredUser] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [subSubCategories, setSubSubCategories] = useState([]);
  const [hasAnnouncement, setHasAnnouncement] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, subRes, ssubRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/admin/categories`),
          axios.get(`${API_BASE_URL}/admin/subcategories`),
          axios.get(`${API_BASE_URL}/admin/subsubcategories`)
        ]);
        setCategories(Array.isArray(catRes.data) ? catRes.data : []);
        setSubCategories(Array.isArray(subRes.data) ? subRes.data : []);
        setSubSubCategories(Array.isArray(ssubRes.data) ? ssubRes.data : []);
      } catch (error) {
        console.error('Error fetching navigation data:', error);
      }
    };

    const checkDiscount = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/admin/order-discounts`);
        if (Array.isArray(res.data) && res.data.length > 0) {
          const active = res.data.find(d => d.is_active === 1);
          setHasAnnouncement(!!active);
        }
      } catch (error) {
        console.error('Error checking for announcement:', error);
      }
    };

    fetchData();
    checkDiscount();
  }, []);

  const structuredCategories = categories.map(cat => ({
    ...cat,
    subCategories: subCategories.filter(sub => sub.category_id === cat.id).map(sub => ({
      ...sub,
      specialized: subSubCategories.filter(ssub => ssub.subcategory_id === sub.id)
    }))
  }));

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    const handleResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);


  const styles = {
    navbar: {
      position: 'fixed',
      top: hasAnnouncement ? '38px' : 0,
      left: 0,
      width: '100%',
      zIndex: 1000,
      height: '70px',
      padding: '0 5%',
      backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.05)',
      boxSizing: 'border-box',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    logo: {
      position: 'relative',
      height: '100%',
      width: '180px',
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
      overflow: 'hidden',
    },
    logoAccent: {
      color: '#ff4757',
    },
    navLinks: {
      display: 'flex',
      gap: '2.5rem',
      alignItems: 'center',
      height: '100%',
      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
      ...(windowWidth <= 992 && {
        position: 'fixed',
        top: 0,
        right: isMenuOpen ? 0 : '-110%',
        height: '100vh',
        width: '320px',
        maxWidth: '85%',
        backgroundColor: '#ffffff',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        padding: '100px 40px 40px',
        boxShadow: '-15px 0 40px rgba(0,0,0,0.1)',
        zIndex: 1002,
        overflowY: 'auto',
        overflowX: 'hidden',
        alignItems: 'flex-start',
      }),
    },
    navLink: (index, isActive) => ({
      textDecoration: 'none',
      color: (hoveredLink === index || isActive) ? '#ff4757' : '#1a1a1a',
      fontWeight: (isActive || windowWidth <= 992) ? 600 : 500,
      fontSize: windowWidth <= 992 ? '1.1rem' : '0.95rem',
      position: 'relative',
      opacity: (hoveredLink === index || isActive) ? 1 : 0.8,
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      ...(windowWidth <= 992 && {
        width: '100%',
        padding: '12px 0',
        borderBottom: '1px solid #f5f5f5',
        letterSpacing: '0.5px',
      })
    }),
    underline: (index, isActive) => ({
      content: '""',
      position: 'absolute',
      bottom: '-4px',
      left: 0,
      width: (hoveredLink === index || isActive) ? '100%' : 0,
      height: '2px',
      backgroundColor: '#ff4757',
      transition: 'all 0.3s ease',
    }),
    actions: {
      display: 'flex',
      gap: '1.5rem',
      alignItems: 'center',
    },
    icon: {
      color: '#1a1a1a',
      cursor: 'pointer',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      transition: 'all 0.3s ease',
    },
    cartCount: {
      position: 'absolute',
      top: '-8px',
      right: '-10px',
      backgroundColor: '#ff4757',
      color: 'white',
      fontSize: '0.7rem',
      padding: '2px 6px',
      borderRadius: '50%',
      fontWeight: 'bold',
    },
    menuToggle: {
      display: windowWidth <= 992 ? 'flex' : 'none',
      cursor: 'pointer',
      flexDirection: 'column',
      gap: '5px',
      zIndex: 1100,
    },
    bar: (index) => {
      const activeStyles = [
        { transform: 'translateY(7px) rotate(45deg)' },
        { opacity: 0 },
        { transform: 'translateY(-7px) rotate(-45deg)' }
      ];
      return {
        width: '25px',
        height: '2px',
        backgroundColor: '#1a1a1a',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        ...(isMenuOpen && activeStyles[index])
      };
    },
    dropdownContainer: {
      position: 'relative',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    },
    dropdownMenu: (isVisible) => ({
      position: 'absolute',
      top: '100%',
      left: '0',
      backgroundColor: 'white',
      minWidth: '180px',
      boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
      borderRadius: '12px',
      padding: '8px 0',
      display: isVisible ? 'block' : 'none',
      zIndex: 1100,
      border: '1px solid #f0f0f0',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      visibility: isVisible ? 'visible' : 'hidden',
    }),
    dropdownItem: (isHovered) => ({
      padding: '10px 18px',
      color: isHovered ? '#ff4757' : '#1a1a1a',
      textDecoration: 'none',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '0.85rem',
      fontWeight: 500,
      backgroundColor: isHovered ? '#fff5f6' : 'transparent',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      flex: 1,
      width: '100%',
      boxSizing: 'border-box',
    }),
    subDropdownMenu: (isVisible) => ({
      position: 'absolute',
      left: '100%',
      top: '0',
      backgroundColor: 'white',
      minWidth: '160px',
      boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
      borderRadius: '12px',
      padding: '8px 0',
      display: isVisible ? 'block' : 'none',
      zIndex: 1101,
      border: '1px solid #f0f0f0',
      marginLeft: '0',
      paddingLeft: '5px',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.3s ease',
      visibility: isVisible ? 'visible' : 'hidden',
    }),
    subDropdownItem: (isHovered) => ({
      padding: '8px 18px',
      color: isHovered ? '#ff4757' : '#555',
      textDecoration: 'none',
      display: 'block',
      fontSize: '0.8rem',
      fontWeight: 400,
      backgroundColor: isHovered ? '#fff5f6' : 'transparent',
      transition: 'all 0.2s ease',
    }),
    profileDropdown: (isVisible) => ({
      position: 'absolute',
      top: '100%',
      right: '0',
      backgroundColor: 'white',
      minWidth: '200px',
      boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
      borderRadius: '12px',
      padding: '12px 0',
      display: isVisible ? 'block' : 'none',
      zIndex: 1100,
      border: '1px solid #f0f0f0',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      visibility: isVisible ? 'visible' : 'hidden',
    })
  };

  const Icons = {
    Search: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      </svg>
    ),
    User: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    ),
    Heart: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>
    ),
    Cart: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1"></circle>
        <circle cx="20" cy="21" r="1"></circle>
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
      </svg>
    )
  };

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Shop', path: '/shop' },
    ...structuredCategories.map(cat => ({
      label: cat.name,
      path: `/shop?catID=${cat.id}`,
      isCategory: true,
      categoryData: cat
    })),
  ];

  return (
    <nav style={styles.navbar}>
      {/* Backdrop for mobile menu */}
      {windowWidth <= 992 && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(8px)',
            zIndex: 900,
            opacity: isMenuOpen ? 1 : 0,
            visibility: isMenuOpen ? 'visible' : 'hidden',
            transition: 'all 0.4s ease',
          }}
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      <Link to="/" style={{ ...styles.logo, overflow: 'hidden' }}>
        <img
          src={logo}
          alt="Mégara"
          style={{
            height: windowWidth <= 768 ? '45px' : '50px',
            objectFit: 'contain',
            transform: 'scale(3.5)',
            marginLeft: '30px',
            marginTop: '35px',
            transition: 'all 0.3s ease',
            filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.1))'
          }}
        />
      </Link>


      <div style={styles.navLinks}>
        {windowWidth <= 992 && (
          <div style={{
            width: '100%',
            marginBottom: '40px',
            paddingBottom: '20px',
            borderBottom: '2px solid #1a1a1a'
          }}>
            <h2 style={{
              fontSize: '1.8rem',
              fontWeight: 900,
              letterSpacing: '3px',
              margin: 0,
              color: '#1a1a1a',
              fontFamily: "'Outfit', sans-serif"
            }}>MÉGARA</h2>
            <p style={{
              fontSize: '0.65rem',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: '5px 0 0',
              opacity: 0.5,
              fontWeight: 600
            }}>Editorial Concept Store</p>
          </div>
        )}

        {navItems.map((item, index) => {
          const isCategory = item.isCategory;
          const categoryData = item.categoryData;

          const isActive = item.path === '/'
            ? location.pathname === '/'
            : item.isCategory
              ? location.search.includes(`catID=${categoryData.id}`)
              : (location.pathname === item.path && location.search === '');

          return (
            <div
              key={item.label}
              style={{
                ...styles.dropdownContainer,
                ...(windowWidth <= 992 && {
                  width: '100%',
                  height: 'auto',
                  alignItems: 'flex-start',
                  flexDirection: 'column'
                })
              }}
              onMouseEnter={() => {
                if (windowWidth > 992) {
                  setHoveredLink(index);
                  if (isCategory) setHoveredCategory(categoryData.id);
                }
              }}
              onMouseLeave={() => {
                if (windowWidth > 992) {
                  setHoveredLink(null);
                  if (isCategory) {
                    setHoveredCategory(null);
                    setHoveredSubCategory(null);
                  }
                }
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-between'
              }}>
                <Link
                  to={item.path}
                  style={styles.navLink(index, isActive)}
                  onClick={() => {
                    setIsMenuOpen(false);
                  }}
                >
                  {item.label}
                  {windowWidth > 992 && <div style={styles.underline(index, isActive)} />}
                </Link>

                {windowWidth <= 992 && isCategory && (
                  <div
                    onClick={() => setHoveredCategory(hoveredCategory === categoryData.id ? null : categoryData.id)}
                    style={{ padding: '10px', cursor: 'pointer' }}
                  >
                    <svg
                      width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2"
                      style={{
                        transform: hoveredCategory === categoryData.id ? 'rotate(180deg)' : 'rotate(0)',
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                )}
              </div>

              {/* Category Dropdown */}
              {isCategory && categoryData.subCategories && categoryData.subCategories.length > 0 && (
                <div style={{
                  ...styles.dropdownMenu(hoveredCategory === categoryData.id),
                  ...(windowWidth <= 992 && {
                    position: 'static',
                    width: '100%',
                    boxShadow: 'none',
                    border: 'none',
                    padding: '0 0 0 15px',
                    display: hoveredCategory === categoryData.id ? 'block' : 'none',
                    opacity: 1,
                    visibility: 'visible',
                    backgroundColor: 'transparent'
                  })
                }}>
                  {categoryData.subCategories.map(sub => (
                    <div
                      key={sub.id}
                      style={{ position: 'relative' }}
                      onMouseEnter={() => windowWidth > 992 && setHoveredSubCategory(sub.id)}
                      onMouseLeave={() => windowWidth > 992 && setHoveredSubCategory(null)}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <Link
                          to={sub.specialized?.length > 0 && windowWidth > 992 ? '#' : `/shop?subCatID=${sub.id}`}
                          style={{
                            ...styles.dropdownItem(hoveredSubCategory === sub.id),
                            ...(windowWidth <= 992 && {
                              padding: '8px 0',
                              fontSize: '0.9rem',
                              backgroundColor: 'transparent',
                              borderBottom: '1px solid #f9f9f9'
                            })
                          }}
                          onClick={(e) => {
                            if (sub.specialized?.length > 0 && windowWidth > 992) {
                              e.preventDefault();
                              setHoveredSubCategory(hoveredSubCategory === sub.id ? null : sub.id);
                            } else {
                              setIsMenuOpen(false);
                            }
                          }}
                        >
                          {sub.name}
                          {sub.specialized && sub.specialized.length > 0 && windowWidth > 992 && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                          )}
                        </Link>

                        {windowWidth <= 992 && sub.specialized && sub.specialized.length > 0 && (
                          <div
                            onClick={() => setHoveredSubCategory(hoveredSubCategory === sub.id ? null : sub.id)}
                            style={{ padding: '8px', cursor: 'pointer' }}
                          >
                            <svg
                              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2"
                              style={{
                                transform: hoveredSubCategory === sub.id ? 'rotate(180deg)' : 'rotate(0)',
                                transition: 'transform 0.3s ease'
                              }}
                            >
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Sub-Category Dropdown (Specialized) */}
                      {sub.specialized && sub.specialized.length > 0 && (
                        <div style={{
                          ...styles.subDropdownMenu(hoveredSubCategory === sub.id),
                          ...(windowWidth <= 992 && {
                            position: 'static',
                            width: '100%',
                            boxShadow: 'none',
                            border: 'none',
                            padding: '0 0 0 15px',
                            display: hoveredSubCategory === sub.id ? 'block' : 'none',
                            opacity: 1,
                            visibility: 'visible',
                            backgroundColor: 'transparent'
                          })
                        }}>
                          {sub.specialized.map(ssub => (
                            <Link
                              key={ssub.id}
                              to={`/shop?subSubID=${ssub.id}`}
                              style={{
                                ...styles.subDropdownItem(false),
                                ...(windowWidth <= 992 && {
                                  padding: '6px 0',
                                  fontSize: '0.8rem',
                                  backgroundColor: 'transparent'
                                })
                              }}
                              onMouseEnter={(e) => {
                                if (windowWidth > 992) {
                                  e.target.style.color = '#ff4757';
                                  e.target.style.backgroundColor = '#fff5f6';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (windowWidth > 992) {
                                  e.target.style.color = '#555';
                                  e.target.style.backgroundColor = 'transparent';
                                }
                              }}
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {ssub.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

      </div>

      <div style={styles.actions}>

        <div
          style={{ ...styles.icon, position: 'relative' }}
          onMouseEnter={() => setHoveredUser(true)}
          onMouseLeave={() => setHoveredUser(false)}
        >
          {user ? (
            <>
              <Icons.User />
              <div style={styles.profileDropdown(hoveredUser)}>
                <div style={{ padding: '12px 18px', borderBottom: '1px solid #f0f0f0', marginBottom: '8px' }}>
                  <p style={{ fontSize: '0.85rem', fontWeight: 600, margin: 0 }}>{user.name}</p>
                  <p style={{ fontSize: '0.75rem', color: '#666', margin: '4px 0 0 0' }}>{user.email}</p>
                </div>
                <Link to="/profile" style={styles.dropdownItem(false)} onClick={() => setHoveredUser(false)}>Profile</Link>
                <Link to="/orders" style={styles.dropdownItem(false)} onClick={() => setHoveredUser(false)}>My Orders</Link>
                <div
                  style={{ ...styles.dropdownItem(false), color: '#ff4757' }}
                  onClick={() => {
                    logout();
                    setHoveredUser(false);
                  }}
                >
                  Logout
                </div>
              </div>
            </>
          ) : (
            <Link to="/auth" style={{ color: 'inherit', display: 'flex', alignItems: 'center' }}>
              <Icons.User />
            </Link>
          )}
        </div>

        <Link to="/wishlist" style={{ ...styles.icon, color: 'inherit' }}><Icons.Heart /></Link>
        <Link to="/cart" style={{ ...styles.icon, color: 'inherit' }}>
          <Icons.Cart />
          <span style={styles.cartCount}>{cartCount}</span>
        </Link>

        <div style={styles.menuToggle} onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <div style={styles.bar(0)}></div>
          <div style={styles.bar(1)}></div>
          <div style={styles.bar(2)}></div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
